/**
 * useEarnings — shared hook for upcoming earnings data.
 *
 * Fetches /api/earnings once per page load and caches the result at module
 * level, so every component that calls this hook shares the same data without
 * firing duplicate requests.
 */
import { useEffect, useState } from "react";
import requests from "../requests";

// Presence-level signal teaser attached by GET /api/earnings (shown to all tiers). Each
// signal carries an `unlock` token; the conviction numbers + analog detail are revealed on
// click via the unlocked detail endpoints, not in the teaser itself.
export interface FeaturedTeaser {
  direction: "up" | "down";
  window: number;      // 10|20|65|130|260
  // Signed per-signal unlock token — pass to buildViewFeaturedUrl so a logged-out click
  // opens this featured signal in full (today's data) past the freemium gate.
  unlock?: string;
}
export interface PatternTeaser {
  pattern_type: string;
  // Navigational — build the /patterns/<ticker>/<type>/<start>?window=<w>&lookback_days=7
  // detail link. window is not displayed in the badge.
  start_date?: string;
  window?: number;
  sentiment?: string;   // 'bullish' | 'bearish' | 'neutral' (shown to all tiers)
  confidence?: number;
  // Signed per-signal unlock token — pass to buildPatternDetailUrl so a logged-out click
  // opens this pattern in full past the freemium gate.
  unlock?: string;
}
export interface MomentumTeaser {
  upr_rank: number;
  upr_rank_chg: number;
}
export interface EarningsSignals {
  featured: FeaturedTeaser | null;
  pattern: PatternTeaser | null;
  momentum_rank: MomentumTeaser | null;
}

export interface EarningsEntry {
  ticker: string;
  name: string;
  date: string;        // "YYYY-MM-DD"
  eps_forecast: string | null;
  signals?: EarningsSignals | null;
}

// Module-level cache — survives across component mounts within the same session.
let _cache: Record<string, EarningsEntry> | null = null;
let _inflight: Promise<void> | null = null;

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + "T00:00:00");
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

// Shared fetch — populates the module cache once and resolves when ready.
// Both useEarnings (ticker lookup) and useUpcomingEarnings (sorted list) await it.
function ensureEarningsLoaded(): Promise<void> {
  if (_cache) return Promise.resolve();
  if (!_inflight) {
    _inflight = requests.get("/api/earnings")
      .then(r => {
        const entries: EarningsEntry[] = r.data.earnings ?? [];
        _cache = {};
        for (const e of entries) {
          // Only keep entries that are upcoming (within next 30 days)
          const d = daysUntil(e.date);
          if (d >= 0 && d <= 30) {
            _cache[e.ticker] = e;
          }
        }
      })
      .catch((err) => {
        // Log internally only; consumers render a generic empty state, never the reason.
        // eslint-disable-next-line no-console
        console.error("Failed to load /api/earnings:", err);
        _cache = {};
      });
  }
  return _inflight;
}

/**
 * Returns a lookup function `getEarnings(ticker)`.
 * Returns null until the data is loaded, then the entry or null if not found.
 * Only entries within the next 30 days are considered "active" for display.
 */
export function useEarnings(): (ticker: string) => EarningsEntry | null {
  const [cache, setCache] = useState<Record<string, EarningsEntry>>(_cache ?? {});

  useEffect(() => {
    ensureEarningsLoaded().finally(() => setCache(_cache ?? {}));
  }, []);

  return (ticker: string) => cache[ticker] ?? null;
}

/**
 * Returns the upcoming earnings entries (next 30 days) sorted by date ascending,
 * nearest first, plus a `loading` flag so callers can reserve space with a
 * skeleton instead of rendering nothing until the fetch lands. Shares the same
 * cache/fetch as useEarnings, so adding this to a page costs no extra request.
 */
export function useUpcomingEarnings(): { entries: EarningsEntry[]; loading: boolean } {
  const [entries, setEntries] = useState<EarningsEntry[]>(
    _cache ? Object.values(_cache) : [],
  );
  // Already cached (a second mount in the same session) → never flash a skeleton.
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    if (_cache) return;
    let alive = true;
    ensureEarningsLoaded().finally(() => {
      if (!alive) return;
      setEntries(Object.values(_cache ?? {}));
      setLoading(false);
    });
    return (): void => {
      alive = false;
    };
  }, []);

  return {
    entries: [...entries].sort((a, b) => a.date.localeCompare(b.date)),
    loading,
  };
}

/** Convenience: how many days until a ticker's next earnings (or null). */
export { daysUntil };
